import { useState } from 'react';
import AppBar from '@mui/material/AppBar';
import Avatar from '@mui/material/Avatar';
import AvatarGroup from '@mui/material/AvatarGroup';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Toolbar from '@mui/material/Toolbar';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import MusicNoteRounded from '@mui/icons-material/MusicNoteRounded';
import GroupRounded from '@mui/icons-material/GroupRounded';
import WorkspacePremiumRounded from '@mui/icons-material/WorkspacePremiumRounded';
import ContentCopyRounded from '@mui/icons-material/ContentCopyRounded';
import CheckRounded from '@mui/icons-material/CheckRounded';
import LogoutRounded from '@mui/icons-material/LogoutRounded';
import { useJamRoom } from '../hooks/useJamRoom.jsx';
import { hueFromString, initials } from '../lib/format.js';

const STATUS = {
  connected: { label: 'Conectado', color: 'success' },
  connecting: { label: 'Conectando…', color: 'warning' },
  reconnecting: { label: 'Reconectando…', color: 'warning' },
  error: { label: 'Sin conexión', color: 'error' },
};

/**
 * Cabecera Material: sala, estado, usuarios en vivo e invitacion.
 */
export default function RoomHeader() {
  const jam = useJamRoom();
  const { room, users, isHost, me, socketStatus } = jam;
  const [copied, setCopied] = useState(false);

  const status = STATUS[socketStatus] ?? STATUS.connecting;

  async function copyCode() {
    if (!room?.id) return;
    const link = `${location.origin}${location.pathname}?room=${room.id}`;
    try {
      await navigator.clipboard.writeText(link);
    } catch {
      /* portapapeles no disponible: el codigo sigue visible al compartir */
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1600);
  }

  return (
    <AppBar
      position="static"
      elevation={0}
      color="transparent"
      sx={{
        bgcolor: 'rgba(19,19,20,0.82)',
        backdropFilter: 'blur(16px)',
        borderBottom: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Toolbar sx={{ gap: { xs: 1, sm: 1.5 }, px: { xs: 1.5, sm: 2.5 }, minHeight: { xs: 60, sm: 68 } }}>
        <Avatar
          variant="rounded"
          sx={{
            width: 38,
            height: 38,
            borderRadius: '12px',
            bgcolor: 'rgba(168,199,250,0.16)',
            color: 'primary.main',
          }}
        >
          <MusicNoteRounded fontSize="small" />
        </Avatar>

        <Box sx={{ minWidth: 0, flex: '1 1 auto' }}>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center', minWidth: 0 }}>
            <Typography variant="subtitle1" noWrap sx={{ fontWeight: 600 }}>
              {room?.name ?? 'Sala'}
            </Typography>
            {isHost && (
              <Chip
                size="small"
                icon={<WorkspacePremiumRounded />}
                label="Anfitrión"
                color="primary"
                variant="outlined"
                sx={{ display: { xs: 'none', sm: 'inline-flex' } }}
              />
            )}
          </Stack>
          <Stack
            direction="row"
            spacing={1}
            sx={{ alignItems: 'center', display: { xs: 'none', sm: 'flex' } }}
          >
            <Box
              sx={{
                width: 7,
                height: 7,
                borderRadius: '50%',
                bgcolor: `${status.color}.main`,
                boxShadow: `0 0 0 3px rgba(255,255,255,0.06)`,
              }}
            />
            <Typography variant="caption" color="text.secondary">
              {status.label}
            </Typography>
          </Stack>
        </Box>

        <Chip
          size="small"
          icon={<GroupRounded />}
          label={users.length}
          variant="outlined"
          sx={{ display: { xs: 'none', sm: 'inline-flex' } }}
        />

        <AvatarGroup
          max={4}
          sx={{
            display: { xs: 'none', md: 'flex' },
            '& .MuiAvatar-root': {
              width: 30,
              height: 30,
              fontSize: 12,
              fontWeight: 600,
              borderColor: 'background.default',
            },
          }}
        >
          {users.map((u) => (
            <Avatar
              key={u.id}
              title={u.isHost ? `${u.name} (anfitrión)` : u.name}
              sx={{
                bgcolor: `hsl(${hueFromString(u.id ?? u.name)} 42% 38%)`,
                ...(u.isHost && { boxShadow: '0 0 0 2px #a8c7fa' }),
                ...(u.id === me.id && { outline: '1px dashed rgba(255,255,255,0.5)' }),
              }}
            >
              {initials(u.name)}
            </Avatar>
          ))}
        </AvatarGroup>

        <Button
          variant="outlined"
          size="small"
          onClick={copyCode}
          startIcon={copied ? <CheckRounded /> : <ContentCopyRounded />}
          sx={{ flex: '0 0 auto', minWidth: { xs: 40, sm: 'auto' }, px: { xs: 1.25, sm: 2 } }}
        >
          <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
            {copied ? 'Copiado' : 'Invitar'}
          </Box>
        </Button>

        <Tooltip title="Salir de la sala">
          <IconButton aria-label="Salir de la sala" onClick={() => jam.leave()} size="small" sx={{ flex: '0 0 auto' }}>
            <LogoutRounded fontSize="small" />
          </IconButton>
        </Tooltip>
      </Toolbar>
    </AppBar>
  );
}
